// Surpresas das portas do Advento: cada porta abre com um efeito diferente (ver o campo effect em seasonAdvent.js).
//   card:  o cartão de festas (os efeitos de "céu" acontecem dentro dele)
//   door:  a porta clicada (os efeitos que "saem da porta" partem do centro dela)
//   badge: a estrela do cartão, que brilha junto em alguns efeitos
//   paper: o papel da cartinha aberta (recebe o brilho que passa e a luz do dia 24)
// Tudo em Web Animations API; nada acontece com prefers-reduced-motion.
import seasonBurst from './seasonBurst';
import {prefersReducedMotion} from './season';

const STAR = 'M0 -11 L3 -3.6 L11 -3.4 L4.8 1.6 L7 9.6 L0 5 L-7 9.6 L-4.8 1.6 L-11 -3.4 L-3 -3.6 Z';
const HEART = 'M0 9 C-11 1 -12 -8 -5.5 -9.6 C-2.8 -10.3 -0.8 -8.6 0 -6.8 C0.8 -8.6 2.8 -10.3 5.5 -9.6 C12 -8 11 1 0 9 Z';
const SVG = 'http://www.w3.org/2000/svg';
const XMAS = ['#c8303f', '#e2546a', '#1b8a72', '#2fd3a7', '#f3d68a', '#fff4cf'];
const HEARTS = ['#e8607a', '#f3d68a', '#ff9fb1', '#fff4cf'];

const random = (min, max) => min + Math.random() * (max - min);

function track(animation, element) {
    animation.onfinish = () => element.remove();
    animation.oncancel = () => element.remove();
    return animation;
}

// Camada dentro do cartão, recortada pelas bordas arredondadas; some sozinha
function stage(card, life) {
    const layer = document.createElement('div');
    layer.className = 'season-effect-stage';
    layer.setAttribute('aria-hidden', 'true');
    card.appendChild(layer);
    setTimeout(() => layer.remove(), life);
    return layer;
}

function shape(path, className, color) {
    const svg = document.createElementNS(SVG, 'svg');
    svg.setAttribute('viewBox', '-12 -12 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.classList.add(className);
    svg.innerHTML = `<path d="${path}"/>`;
    if (color) svg.style.fill = color;
    return svg;
}

function center(element) {
    const box = element.getBoundingClientRect();
    return {x: box.left + box.width / 2, y: box.top + box.height / 2};
}

function glowBadge(badge, delay = 0) {
    badge?.animate?.([
        {transform: 'scale(1) rotate(0deg)', filter: 'brightness(1)'},
        {transform: 'scale(1.18) rotate(-8deg)', filter: 'brightness(1.35) drop-shadow(0 0 12px rgba(243, 214, 138, 0.9))', offset: 0.35},
        {transform: 'scale(1) rotate(0deg)', filter: 'brightness(1)'},
    ], {duration: 1100, delay, easing: 'ease-out'});
}

// Faíscas de neve e ouro saindo da porta
function sparkle({door}) {
    seasonBurst(door, {origin: {x: 0.5, y: 0.5}, amount: 20});
}

// Neve fininha caindo por todo o cartão
function snow({card}, amount = 46) {
    const layer = stage(card, 5200);
    const {width, height} = card.getBoundingClientRect();
    for (let i = 0; i < amount; i++) {
        const flake = document.createElement('span');
        const size = random(2, 6);
        flake.className = 'season-effect-flake';
        Object.assign(flake.style, {left: `${random(0, width)}px`, width: `${size}px`, height: `${size}px`});
        layer.appendChild(flake);
        const sway = random(-30, 30);
        track(flake.animate([
            {transform: 'translate(0, -10px)', opacity: 0},
            {transform: `translate(${sway * 0.4}px, ${height * 0.3}px)`, opacity: 1, offset: 0.25},
            {transform: `translate(${-sway * 0.3}px, ${height * 0.7}px)`, opacity: 0.9, offset: 0.7},
            {transform: `translate(${sway}px, ${height + 10}px)`, opacity: 0},
        ], {duration: random(2400, 3800), delay: random(0, 1300), easing: 'linear', fill: 'both'}), flake);
    }
}

// Corações subindo da porta, balançando como balões
function hearts({door}) {
    const from = center(door);
    for (let i = 0; i < 12; i++) {
        const heart = shape(HEART, 'season-effect-heart', HEARTS[i % HEARTS.length]);
        const size = random(10, 18);
        Object.assign(heart.style, {left: `${from.x - size / 2}px`, top: `${from.y - size / 2}px`, width: `${size}px`, height: `${size}px`});
        document.body.appendChild(heart);
        const x = random(-60, 60);
        const rise = random(70, 140);
        track(heart.animate([
            {transform: 'translate(0, 0) scale(0.2) rotate(0deg)', opacity: 0},
            {transform: `translate(${x * 0.3}px, ${-rise * 0.3}px) scale(1) rotate(${x * 0.2}deg)`, opacity: 1, offset: 0.25},
            {transform: `translate(${x * 0.8}px, ${-rise * 0.75}px) scale(1) rotate(${-x * 0.15}deg)`, opacity: 0.9, offset: 0.7},
            {transform: `translate(${x}px, ${-rise}px) scale(0.8) rotate(0deg)`, opacity: 0},
        ], {duration: random(1500, 2300), delay: i * 60, easing: 'cubic-bezier(0.2, 0.6, 0.4, 1)', fill: 'both'}), heart);
    }
}

// Estrela cadente atravessando o cartão e deixando brilhinhos pelo caminho
function comet({card}, delay = 0) {
    const layer = stage(card, 3600 + delay);
    const {width, height} = card.getBoundingClientRect();
    const start = {x: -140, y: random(height * 0.05, height * 0.3)};
    const end = {x: width + 60, y: start.y + random(height * 0.35, height * 0.6)};
    const angle = Math.atan2(end.y - start.y, end.x - start.x) * 180 / Math.PI;
    const tail = document.createElement('span');
    tail.className = 'season-effect-comet';
    Object.assign(tail.style, {left: `${start.x}px`, top: `${start.y}px`});
    layer.appendChild(tail);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const duration = 1100;
    // A cabeça fica na ponta direita da cauda (140px), que gira presa pela esquerda
    const head = {x: 140 * Math.cos(angle * Math.PI / 180), y: 140 * Math.sin(angle * Math.PI / 180)};
    track(tail.animate([
        {transform: `translate(0, 0) rotate(${angle}deg)`, opacity: 0},
        {transform: `translate(${dx * 0.1}px, ${dy * 0.1}px) rotate(${angle}deg)`, opacity: 1, offset: 0.1},
        {transform: `translate(${dx}px, ${dy}px) rotate(${angle}deg)`, opacity: 0.6},
    ], {duration, delay, easing: 'cubic-bezier(0.4, 0, 0.7, 1)', fill: 'both'}), tail);
    // Os brilhinhos acendem atrás da cabeça da estrela
    for (let i = 1; i <= 9; i++) {
        const t = i / 10;
        twinkle(layer, start.x + head.x + dx * t + random(-6, 6), start.y + head.y + dy * t + random(-8, 8),
            delay + duration * t * 0.9, random(6, 11));
    }
}

function twinkle(layer, x, y, delay, size = 10) {
    const star = shape(STAR, 'season-effect-star');
    Object.assign(star.style, {left: `${x - size / 2}px`, top: `${y - size / 2}px`, width: `${size}px`, height: `${size}px`});
    layer.appendChild(star);
    track(star.animate([
        {transform: 'scale(0) rotate(0deg)', opacity: 0},
        {transform: 'scale(1.2) rotate(45deg)', opacity: 1, offset: 0.3},
        {transform: 'scale(0.9) rotate(90deg)', opacity: 0.9, offset: 0.6},
        {transform: 'scale(0) rotate(160deg)', opacity: 0},
    ], {duration: random(1000, 1500), delay, easing: 'ease-out', fill: 'both'}), star);
}

// Ondas de luz dourada saindo da porta, e a estrela do cartão responde
function halo({door, badge}) {
    const from = center(door);
    for (let i = 0; i < 3; i++) {
        const ring = document.createElement('span');
        ring.className = 'season-effect-ring';
        Object.assign(ring.style, {left: `${from.x}px`, top: `${from.y}px`});
        document.body.appendChild(ring);
        track(ring.animate([
            {transform: 'translate(-50%, -50%) scale(0.2)', opacity: 0.95},
            {transform: 'translate(-50%, -50%) scale(4.2)', opacity: 0},
        ], {duration: 1300, delay: i * 220, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)', fill: 'both'}), ring);
    }
    seasonBurst(door, {origin: {x: 0.5, y: 0.5}, amount: 8});
    glowBadge(badge, 300);
}

// Brilho que atravessa o papel da cartinha, como luz batendo num cartão dourado
function sheen(paper, delay = 0) {
    if (!paper) return;
    const band = document.createElement('span');
    band.className = 'season-effect-sheen';
    paper.appendChild(band);
    track(band.animate([
        {transform: 'translateX(-120%) skewX(-18deg)', opacity: 0},
        {transform: 'translateX(-40%) skewX(-18deg)', opacity: 1, offset: 0.25},
        {transform: 'translateX(320%) skewX(-18deg)', opacity: 0},
    ], {duration: 1600, delay, easing: 'cubic-bezier(0.45, 0, 0.3, 1)', fill: 'both'}), band);
}

// Faixa de luz dourada varrendo o cartão de ponta a ponta (e passando pela cartinha)
function shimmer({card, badge, paper}, delay = 0) {
    sheen(paper, delay + 350);
    const layer = stage(card, 2400 + delay);
    const {width} = card.getBoundingClientRect();
    const band = document.createElement('span');
    band.className = 'season-effect-shimmer';
    layer.appendChild(band);
    track(band.animate([
        {transform: 'translateX(-260px) skewX(-20deg)', opacity: 0},
        {transform: `translateX(${width * 0.15}px) skewX(-20deg)`, opacity: 1, offset: 0.2},
        {transform: `translateX(${width + 60}px) skewX(-20deg)`, opacity: 0.8},
    ], {duration: 1700, delay, easing: 'cubic-bezier(0.45, 0, 0.3, 1)', fill: 'both'}), band);
    glowBadge(badge, delay + 200);
}

// Confete de Natal: vermelho, verde e dourado
function ribbons({door}) {
    seasonBurst(door, {newYear: true, palette: XMAS, origin: {x: 0.5, y: 0.5}, amount: 34});
}

// Estrelinhas acendendo e apagando pelo cartão inteiro
function stars({card}, amount = 20, spread = 1700) {
    const layer = stage(card, spread + 1800);
    const {width, height} = card.getBoundingClientRect();
    for (let i = 0; i < amount; i++) {
        twinkle(layer, random(10, width - 10), random(8, height - 8), random(0, spread), random(8, 16));
    }
}

// Véspera de Natal: tudo junto, em sequência, e a cartinha ganha uma luz dourada que respira
function grand(context) {
    context.paper?.parentElement?.classList.add('is-glowing');
    shimmer(context);
    sparkle(context);
    setTimeout(() => stars(context, 28, 2400), 350);
    comet(context, 700);
    setTimeout(() => snow(context, 60), 500);
    setTimeout(() => ribbons(context), 1250);
}

const EFFECTS = {sparkle, snow, hearts, comet, halo, shimmer, ribbons, stars, grand};

export default function playDoorEffect(effect, {card, door, badge, paper} = {}) {
    if (!card?.animate || !door || prefersReducedMotion()) return;
    (EFFECTS[effect] || sparkle)({card, door, badge, paper});
    // Toda surpresa termina com um brilho discreto passando pelo papel da cartinha
    if (!['shimmer', 'grand'].includes(effect)) sheen(paper, 900);
}
